import { LinearGradient } from "expo-linear-gradient";
import { Box, Image, Pressable, Text, useTheme, VStack } from "native-base";
import React from "react";
import { useTranslation } from "react-i18next";

import { getSimpleRecipeImage } from "../helpers/generalHelpers";
import { getTotalRecipeTime } from "../helpers/simpleRecipeHelpers";
import type { MealSlotSpecification, SimpleRecipeMeal } from "../services/backendTypes";

type SimpleRecipeCardProps = {
  simpleRecipeMeal: SimpleRecipeMeal;
  index: number;
  mealSlotSpecification: MealSlotSpecification;
  onButtonPress: () => void;
  onOpenAction?: () => void;
};

const SimpleRecipeCard = ({
  simpleRecipeMeal,
  index,
  onButtonPress,
  onOpenAction,
  mealSlotSpecification,
}: SimpleRecipeCardProps): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Helper function to format meal type
  const formatMealType = (mealType: string): string => {
    switch (mealType.toLowerCase()) {
      case "breakfast":
        return "Breakfast";
      case "lunch":
        return "Lunch";
      case "dinner":
        return "Dinner";
      case "snack":
        return "Snack";
      default:
        return mealType;
    }
  };

  return (
    <Pressable onPress={onButtonPress} nativeID={`simpleRecipeMeal-${index}`} flex={1}>
      <Box
        borderRadius={theme.borderRadii.card.default}
        mx={1}
        mb={3}
        overflow="hidden"
        flex={1}
        shadow={10}
        position="relative"
        style={{ aspectRatio: 173 / 255 }}
      >
        {/* Full Coverage Background Image */}
        <Image
          source={getSimpleRecipeImage(simpleRecipeMeal).source}
          alt={simpleRecipeMeal.simple_recipe.name}
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          width="100%"
          height="100%"
          resizeMode="cover"
        />

        {/* Gradient Overlay for Text Readability */}
        <LinearGradient
          colors={["transparent", `${theme.colors.function[50]}CC`, theme.colors.function[50]]}
          locations={[0.3, 0.7, 1]}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "70%",
          }}
        />

        {/* Icons Container - Top Right */}
        {Boolean(onOpenAction) && (
          <Box position="absolute" top={2} right={2}>
            {/* Icons would go here if they exist in the original design */}
          </Box>
        )}

        {/* Text Overlay Container */}
        <VStack
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          px={2}
          pb={2}
          pt={1}
          space={1}
          testID={`simpleRecipeMeal-${simpleRecipeMeal.simple_recipe.id || "MISSING_ID"}`}
        >
          {/* Recipe Name */}
          <Text
            fontSize="xl"
            fontStyle="italic"
            color="function.10"
            fontFamily={theme.fonts.serif}
            numberOfLines={2}
            testID="simpleRecipeMealName"
          >
            {simpleRecipeMeal.simple_recipe.name}
          </Text>

          {/* Total Time (Prep + Cook) */}
          <Text fontSize="sm" fontWeight="600" color="function.10" fontFamily={theme.fonts.body}>
            {getTotalRecipeTime(simpleRecipeMeal.simple_recipe)} mins
          </Text>
        </VStack>
      </Box>
    </Pressable>
  );
};

export default SimpleRecipeCard;
